import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { AuthUserVerifyEmailRequest } from "../../models/AuthUserVerifyEmailRequest";
import { UtilService } from "../../services/UtilService";
import { ReactComponent as SvgLogo } from "../../components/Header/logo.svg";
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { BaseControlProps } from "../../models/BaseControlProps";
import { AuthAPIService } from "../../services/AuthAPIServices";

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
      'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
      width: '450px',
    },
    ...theme.applyStyles('dark', {
      boxShadow:
        'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
  }));
  
const MainContainer = styled(Stack)(({ theme }) => ({
    padding: 20,
    marginTop: '10vh',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      inset: 0
    },
}));

const VerifyEmail = (props: BaseControlProps) => {
    const [scope, apply] = React.useState<AuthUserVerifyEmailRequest>({
        encryptedEmailAddress: ""
    })
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isVerifying, setIsVerifying] = useState(true);
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const [verificationSuccess, setVerificationSuccess] = useState(false);
    const authService = new AuthAPIService();

    const verifyEmail = async () => {

        authService.veryEmail(scope).then((response) => {
            setIsVerifying(false);
            if (response.error) {
                setVerificationError(response.error);
            }
            else {
                if (response.response?.success)  {
                    setVerificationSuccess(true);

                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                }
                else {
                    setVerificationError("Error verifying email, please try again later.");
                }
            }
        })
    }

    useEffect(() => {
        const emailAddress = searchParams.get('EmailAddress');
        scope.encryptedEmailAddress = emailAddress ? emailAddress : '';
        applyScope();

        const verifyEmailAsync = async () => {
            await verifyEmail();
        }

        if (!emailAddress) {
            if (props.isMessageInfoShow && props.setMessageInfo) {
                props.setMessageInfo(UtilService.getErrorMessageInfo("Invalid email verification link."));
                props.isMessageInfoShow(true);
            }
            navigate('/login');
        }
        else {
            verifyEmailAsync();
        }

    }, [searchParams]);

    function applyScope(){
        apply(UtilService.cloneObject(scope));
    }

    return (
        <MainContainer direction="column" justifyContent="space-between">
            <Card variant="outlined">
                <div className="logo-container">
                    <SvgLogo focusable={false} viewBox="0 0 500 150" />
                </div>
                <Typography
                    component="h1"
                    variant="h6"
                >
                    Email Verification
                </Typography>
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                    {isVerifying ? (
                        <>
                            <Typography component="span" variant="subtitle2" sx={{ mt: 2, color: 'text.primary' }}>
                                Verifying your email...
                            </Typography>
                        </>
                    ) : verificationSuccess ? (
                        <>
                            <Typography component="span" variant="subtitle2" sx={{ color: 'green' }}>
                                Email verified successfully!
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{ mt: 4 }}
                                onClick={() => { navigate('/login'); }}
                            >
                                Go to Login
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography component="span" variant="subtitle2" sx={{ color: 'text.primary' }}>
                                {verificationError}
                            </Typography>
                        </>
                    )}
                </Box>
            </Card>
        </MainContainer>
    );
};

export default VerifyEmail;